// ============================================================================
// CANCUN100 — arranque.js  (APP 2/17: arranque a prueba de datos viejos)
// ----------------------------------------------------------------------------
// 1) migrarAlmacen(): una sola vez por navegador (c100-version = 2.0) borra las llaves c100-* / admin-* / sb-b2b-*
//    que la 2.0 ya no usa. No toca la sesión de Supabase (sb-<ref>-auth-token) ni las llaves vivas.
// 2) desregistrarServiceWorker(): si quedó un service worker de otra época, fuera; nunca se vuelve a registrar.
// 3) recargarSiChunkPerdido(): un chunk que ya no existe (deploy nuevo encima de un index viejo) → una recarga, no dos.
// 4) <ArranqueSeguro>: ErrorBoundary de la raíz. Si algo truena al pintar, en vez de negro: «Algo falló al cargar ·
//    Reintentar». El botón limpia las llaves c100-* / admin-* y recarga. El error se registra en consola.
// ============================================================================
import React from 'react';

export const VERSION_ALMACEN = '2.0';
const LLAVE_VERSION = 'c100-version';
const LLAVE_RECARGA = 'c100-recarga-chunk';

// Llaves que la 2.0 sí usa (exactas o por prefijo). Todo lo demás con prefijo c100- / admin- / sb-b2b- se borra una vez.
const VIVAS_EXACTAS = new Set([
  LLAVE_VERSION,
  'c100-sesion',          // sesión que sobrevive al F5 (App.js)
  'c100-tema',            // claro/oscuro del agente
  'admin-theme',          // claro/oscuro del admin
  'admin-menu',           // menú arriba/lateral del admin (APP 2/09)
  'c100-equipo',          // último equipo en este navegador (temaEquipo.js)
  'c100-equipo-doble',    // Sala ↔ Top Tours (APP 2/13)
  'c100-menu-mini',       // menú lateral plegado (MenuLateral.js)
  'c100-idioma-login',    // idioma del login (LoginPantalla.js)
  'c100-ultimo-correo',   // «Recuérdame»
  'cancun100_misventas_view',
]);
const VIVAS_PREFIJOS = ['c100-panel-', 'c100-bienvenida-', 'c100:borrador:'];
const BORRABLES_PREFIJOS = ['c100-', 'c100:', 'admin-', 'sb-b2b-'];

const esViva = (k) => VIVAS_EXACTAS.has(k) || VIVAS_PREFIJOS.some((p) => k.startsWith(p));
const esBorrable = (k) => BORRABLES_PREFIJOS.some((p) => k.startsWith(p));

export const migrarAlmacen = () => {
  try {
    if (localStorage.getItem(LLAVE_VERSION) === VERSION_ALMACEN) return [];
    const borradas = [];
    for (const k of Object.keys(localStorage)) {
      if (esBorrable(k) && !esViva(k)) { localStorage.removeItem(k); borradas.push(k); }
    }
    localStorage.setItem(LLAVE_VERSION, VERSION_ALMACEN);
    if (borradas.length) console.info('[arranque] almacén migrado a ' + VERSION_ALMACEN + '; llaves viejas borradas:', borradas.join(', '));
    return borradas;
  } catch (e) { return []; }
};

// Limpieza fuerte (botón «Reintentar»): todas las llaves de la app, sesión incluida; la de Supabase se queda.
export const limpiarLlavesApp = () => {
  try { for (const k of Object.keys(localStorage)) { if (esBorrable(k) || k === 'cancun100_misventas_view') localStorage.removeItem(k); } } catch (e) { /* sin storage */ }
  try { sessionStorage.removeItem(LLAVE_RECARGA); } catch (e) { /* sin storage */ }
};

export const desregistrarServiceWorker = () => {
  try {
    if (!('serviceWorker' in navigator)) return;
    navigator.serviceWorker.getRegistrations().then((rs) => rs.forEach((r) => r.unregister().catch(() => {}))).catch(() => {});
    if (typeof caches !== 'undefined' && caches.keys) caches.keys().then((ks) => ks.forEach((k) => caches.delete(k).catch(() => {}))).catch(() => {});
  } catch (e) { /* sin SW */ }
};

export const esErrorDeChunk = (e) => {
  const n = String((e && e.name) || ''); const m = String((e && e.message) || e || '');
  return n === 'ChunkLoadError' || /Loading (CSS )?chunk [\w-]+ failed|ChunkLoadError|Failed to fetch dynamically imported module/i.test(m);
};

// Una sola recarga por pestaña (sessionStorage) para no entrar en bucle si el chunk de verdad no existe.
export const recargarUnaVez = () => {
  try {
    if (sessionStorage.getItem(LLAVE_RECARGA) === '1') return false;
    sessionStorage.setItem(LLAVE_RECARGA, '1');
  } catch (e) { return false; }
  window.location.reload();
  return true;
};

export const recargarSiChunkPerdido = () => {
  const manejar = (ev) => {
    const err = ev && (ev.reason || ev.error || ev.message);
    if (esErrorDeChunk(err)) { console.warn('[arranque] chunk perdido; recargando una vez'); recargarUnaVez(); }
  };
  window.addEventListener('error', manejar);
  window.addEventListener('unhandledrejection', manejar);
};

export class ArranqueSeguro extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) {
    console.error('[arranque] la app tronó al pintar:', error, info && info.componentStack);
    if (esErrorDeChunk(error) && recargarUnaVez()) return;
  }
  reintentar = () => { limpiarLlavesApp(); window.location.reload(); };
  render() {
    if (!this.state.error) return this.props.children;
    const msg = String((this.state.error && this.state.error.message) || this.state.error || '');
    return (
      <div role="alert" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, background: '#0b0b12', color: '#f4f4f8', fontFamily: 'system-ui, sans-serif', textAlign: 'center' }}>
        <div style={{ maxWidth: 420 }}>
          <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 8 }}>Algo falló al cargar</div>
          <div style={{ fontSize: 14, opacity: .8, marginBottom: 18 }}>Se limpian los datos guardados de esta app en el navegador y se vuelve a cargar. Tendrás que entrar otra vez.</div>
          <button type="button" onClick={this.reintentar} style={{ fontSize: 16, fontWeight: 700, padding: '12px 22px', borderRadius: 12, border: 0, cursor: 'pointer', background: '#e8456b', color: '#fff' }}>Reintentar</button>
          {msg && <div style={{ fontSize: 12, opacity: .55, marginTop: 18, wordBreak: 'break-word' }}>{msg}</div>}
        </div>
      </div>
    );
  }
}
